// distillation of https://github.com/adobe/react-spectrum/blob/main/packages/%40react-aria/live-announcer/src/LiveAnnouncer.tsx

import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { PreventUserSelectSansCursor } from '../styles/PreventUserSelect';
import { ElemAttrs } from './HtmlAttrs';

export type Assertiveness = 'assertive' | 'polite';
export const DefaultRemovalTimeout = 7000;

// exported as an object so it can be spied on for unit testing
export const LiveAnnouncer = {
  /**
   * @param message The message to announce to assistive technology.
   * @param assertiveness
   * `'assertive'` will interrupt any current announcement.
   * `'polite'` will wait to speak until the user is idle.
   * @param removalTimeout The number of milliseconds to wait after announcing the message, before removing it from the DOM.
   */
  announce: function (message: string, assertiveness: Assertiveness = 'assertive', removalTimeout: number = DefaultRemovalTimeout): void {
    ensureInstance(announcer => announcer.announce(message, assertiveness, removalTimeout));
  },

  /**
   * @param assertiveness the assertiveness queue to clear, or `undefined` for all
   */
  clear: function (assertiveness?: Assertiveness): void {
    ensureInstance(announcer => announcer.clear(assertiveness));
  },

  /** Removes the announcer from the DOM. */
  destroy: function (): void {
    if (AnnouncerRef.current && mountPoint) {
      ReactDOM.unmountComponentAtNode(mountPoint);
      document.body.removeChild(mountPoint);
      mountPoint = null;
    }
  }
};

interface Announcer {
  announce(message: string, assertiveness: Assertiveness, removalTimeout: number): void;
  clear(assertiveness?: Assertiveness): void;
}
const AnnouncerRef = React.createRef<Announcer>();
let mountPoint: HTMLElement | null = null;

/** Enforce a singleton so we don't end up with competing aria-live regions. */
function ensureInstance(callback: (announcer: Announcer) => void): void {
  if (!AnnouncerRef.current) {
    mountPoint = document.createElement('div');
    mountPoint.dataset.liveAnnouncer = 'true';
    document.body.prepend(mountPoint);
    ReactDOM.render(
      <LiveRegionAnnouncer ref={AnnouncerRef} />,
      mountPoint,
      () => callback(AnnouncerRef.current!)
    );
  } else {
    callback(AnnouncerRef.current);
  }
}

// the brave little invisible toaster.gif
let messageId: number = 0;
type LiveRegionMessage = { text: string, id: number };
const LiveRegionAnnouncer = React.forwardRef<Announcer>((_, ref) => {
  const [assertiveMessages, setAssertiveMessages] = React.useState<Array<LiveRegionMessage>>([]);
  const [politeMessages, setPoliteMessages] = React.useState<Array<LiveRegionMessage>>([]);

  React.useImperativeHandle(ref, (): Announcer => ({
    announce: (message, assertiveness = 'assertive', removalTimeout) => {
      if (!message) { return; }
      const id = messageId++;
      const setMessages = assertiveness === 'assertive' ? setAssertiveMessages : setPoliteMessages;

      setMessages(messages => [...messages, { id, text: message }]);
      window.setTimeout(() => {
        if (AnnouncerRef.current) { setMessages(messages => messages.filter(msg => msg.id !== id)); }
      }, removalTimeout);
    },
    clear: (assertiveness) => {
      if (!assertiveness || assertiveness === 'assertive') {
        setAssertiveMessages([]);
      }

      if (!assertiveness || assertiveness === 'polite') {
        setPoliteMessages([]);
      }
    }
  }));

  return <>
    <MessageLog aria-live='assertive' key='assertive'>
      {assertiveMessages.map(message => <div key={message.id}>{message.text}</div>)}
    </MessageLog>
    <MessageLog aria-live='polite' key='polite'>
      {politeMessages.map(message => <div key={message.id}>{message.text}</div>)}
    </MessageLog>
  </>;
});

// personally I find this technique ham-fisted --ckovatch
const VisuallyHiddenStyle: React.CSSProperties = {
  ...PreventUserSelectSansCursor,
  border: 0,
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  margin: '0 -1px -1px 0',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  width: 1,
  whiteSpace: 'nowrap',
};

const MessageLog: React.FC<ElemAttrs> = ({ style, ...props }) =>
  <div
    {...props}
    aria-relevant='additions'
    role='log'
    style={{ ...VisuallyHiddenStyle, ...style }}
  />;
MessageLog.defaultProps = { 'aria-live': 'assertive' };
MessageLog.displayName = 'MessageLog';
